import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Pill, Suma } from '@/ui/primitives'
import { DataTable, type Coloana, type Sortare } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { eDatorie, lei, pereche, perecheDinLei, sursaCurs, PRAG_REZIDUU_LEI,
  type ContextCurs, type ModCurs } from '@/lib/money'
import { citesteMod, CursSelector } from '@/ui/curs-selector'

type Row = {
  id: number
  number: string
  partner: string | null
  total_value: number
  currency: string
  exchange_rate: number | null
  invoice_type: string
  total_value_ron: number
  payments_ron: number
  restant_ron: number
  issue_date: string | null
  due_date: string | null
  status: string | null
  booking_number: string | null
  destination: string | null
}
type Page = { rows: Row[]; total: number; page: number; per_page: number; total_pages: number; eur_rate: number }

/** Vizualizarile pe care le foloseste omul, nu filtrele pe care le expune baza de date. */
const VIEWS = [
  { id: 'open', label: 'De incasat', status: 'open' },
  { id: 'all', label: 'Toate', status: 'all' },
  { id: 'paid', label: 'Achitate', status: 'paid' },
] as const

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'

function scadenta(due: string | null, rest: number) {
  if (!eDatorie(rest)) return <Badge tone="good">Achitat</Badge>
  if (!due) return <Badge tone="neutral">Fara scadenta</Badge>
  const zile = Math.round((new Date(due).getTime() - Date.now()) / 86_400_000)
  if (zile < 0) return <Badge tone="bad">Restant {Math.abs(zile)} z</Badge>
  if (zile <= 5) return <Badge tone="warn">Scade in {zile} z</Badge>
  return <Badge tone="neutral">{zi(due)}</Badge>
}

export function Invoices() {
  const navigheaza = useNavigate()
  const [view, setView] = useState<(typeof VIEWS)[number]['id']>('open')
  const [tip, setTip] = useState<'Sales' | 'Purchase'>('Sales')
  const [q, setQ] = useState('')
  const [pagina, setPagina] = useState(0)
  const [pePagina, setPePagina] = useState(25)
  const [sort, setSort] = useState<Sortare>({ cheie: 'issue_date', sens: 'desc' })
  const [mod, setMod] = useState<ModCurs>(citesteMod)
  const [manual, setManual] = useState<number | null>(null)

  const status = VIEWS.find((v) => v.id === view)!.status
  const { data, isPending } = useQuery({
    queryKey: ['invoices', tip, status, q, pagina, pePagina, sort],
    queryFn: () =>
      api.get<Page>('/invoices/page', {
        invoice_type: tip, status, q, page: pagina + 1, per_page: pePagina,
        sort: sort.cheie, sort_dir: sort.sens,
      }),
    placeholderData: keepPreviousData,
  })

  const rows = data?.rows ?? []
  const ctx: ContextCurs = { bnrPlus2: data?.eur_rate ?? 0, mod, manual }
  const cursZi = data?.eur_rate ?? 0
  const restTotal = rows.reduce((s, r) => s + (eDatorie(r.restant_ron) ? r.restant_ron : 0), 0)

  const coloane: Coloana<Row>[] = [
    { cheie: 'number', antet: 'Numar', sortabil: true, cheieServer: 'number',
      randare: (r) => (
        <span>
          <span className="tnum font-medium">{r.number}</span>
          {r.booking_number && <span className="ml-2 text-[12px] text-muted">rez. {r.booking_number}</span>}
        </span>
      ) },
    { cheie: 'partner', antet: 'Partener', sortabil: true, cheieServer: 'partner',
      randare: (r) => <span className="block max-w-[280px] truncate">{r.partner ?? '—'}</span> },
    { cheie: 'issue_date', antet: 'Emisa', sortabil: true, cheieServer: 'issue_date',
      randare: (r) => <span className="text-muted tnum text-[13px]">{zi(r.issue_date)}</span> },
    { cheie: 'total_value_ron', antet: 'Total', sortabil: true, cheieServer: 'total_value_ron', aliniat: 'dreapta',
      randare: (r) => <Suma {...pereche(r.total_value, r, ctx)} explicatie={sursaCurs(r, ctx)} ton="strong" /> },
    { cheie: 'restant_ron', antet: 'Rest', sortabil: true, cheieServer: 'restant_ron', aliniat: 'dreapta',
      randare: (r) => (eDatorie(r.restant_ron)
        ? <Suma {...perecheDinLei(r.restant_ron, r, ctx)} explicatie={sursaCurs(r, ctx)} />
        : <span className="text-muted">—</span>) },
    { cheie: 'due_date', antet: 'Scadenta', sortabil: true, cheieServer: 'due_date',
      randare: (r) => scadenta(r.due_date, r.restant_ron) },
  ]

  return (
    <>
      <PageHeader
        title="Facturi"
        count={data ? `${data.total.toLocaleString('ro-RO')} documente` : undefined}
        actions={
          <>
            <CursSelector ctx={ctx} onMod={setMod} onManual={setManual} />
            <Button variant="quiet" onClick={() => setTip(tip === 'Sales' ? 'Purchase' : 'Sales')}>
              {tip === 'Sales' ? 'Vanzare' : 'Cumparare'}
            </Button>
            <NewMenu />
          </>
        }
      />

      <div className="px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          {VIEWS.map((v) => (
            <Pill key={v.id} active={view === v.id} onClick={() => { setView(v.id); setPagina(0) }}>
              {v.label}
            </Pill>
          ))}
          <div className="ml-auto flex items-center gap-3">
            {restTotal > 0 && (
              <span className="text-[13.5px] text-muted tnum">
                rest pe pagina {lei(restTotal)} lei · {lei(restTotal / (cursZi || 1))} EUR
              </span>
            )}
          </div>
        </div>

        <p className="text-[12.5px] text-muted">
          Sumele se arata in ambele monede, cu cursul ales sus (implicit: BNR + 2% la vanzare —
          azi {lei(cursZi)} lei/EUR — si cursul furnizorului la cumparare). Cursul folosit pentru
          fiecare suma se vede la hover. Restul sub {PRAG_REZIDUU_LEI} lei e reziduu de curs, nu datorie.
        </p>

        <DataTable
          randuri={rows}
          coloane={coloane}
          cheie={(r) => r.id}
          incarca={isPending}
          peRand={(r) => navigheaza(`/bani/facturi/${r.id}`)}
          placeholderCautare="Numar, partener, comanda…"
          gol={{ titlu: 'Nicio factura aici', indiciu: 'Schimba vizualizarea sau cauta altceva.' }}
          server={{
            total: data?.total ?? 0, pagina, pePagina, setPagina, setPePagina,
            termen: q, setTermen: (v) => { setQ(v); setPagina(0) },
            sort, setSort,
          }}
        />
      </div>
    </>
  )
}
